import { type Middleware } from '@reduxjs/toolkit';

import {
  resetSelectedItemsAction,
  type SelectableItem,
  selectAllSelectableItemsAction,
  selectItemAction,
  selectItemsByMouseAction,
  selectShiftSelectedHighlightedItemsAction,
  setSelectableItemsAction,
  unselectItemAction,
} from '@air/redux-selected-items';

import { PERSISTED_SELECTIONS } from '~/constants/localStorageKeys';
import { type AirState } from '~/store/types';
import { appStorage } from '~/utils/Storage';

export const loadPersistedSelections = (): SelectableItem[] => {
  const stored = appStorage.getItem(PERSISTED_SELECTIONS);

  if (!stored) {
    return [];
  }

  try {
    return JSON.parse(stored);
  } catch {
    return [];
  }
};

/**
 * Enriches items with selectedBoard metadata (tracking which board each item was selected from)
 * and persists selections to localStorage for cross-board selection support.
 */
export const selectedItemsPersistenceMiddleware: Middleware<Record<string, never>, AirState> =
  (store) => (next) => (action) => {
    let updatedAction = action;

    if (
      selectItemAction.match(action) ||
      selectItemsByMouseAction.match(action) ||
      selectShiftSelectedHighlightedItemsAction.match(action) ||
      setSelectableItemsAction.match(action)
    ) {
      const state = store.getState();
      const currentBoardData = state.centralizedBoard.board;
      const persistedSelections = loadPersistedSelections();
      const persistedItemsMap = new Map(persistedSelections.map((item) => [item.id, item.selectedBoard]));

      const enrichItem = (item: SelectableItem): SelectableItem => {
        if (persistedItemsMap.has(item.id)) {
          return {
            ...item,
            selectedBoard: persistedItemsMap.get(item.id),
          };
        }

        return {
          ...item,
          selectedBoard: currentBoardData
            ? {
                id: currentBoardData.id,
                name: currentBoardData.title,
              }
            : undefined,
        };
      };

      if (selectItemAction.match(action)) {
        updatedAction = {
          ...action,
          payload: {
            item: enrichItem(action.payload.item),
          },
        };
      } else if (selectItemsByMouseAction.match(action)) {
        updatedAction = {
          ...action,
          payload: {
            items: action.payload.items.map(enrichItem),
          },
        };
      } else if (selectShiftSelectedHighlightedItemsAction.match(action)) {
        updatedAction = {
          ...action,
          payload: {
            nextAnchor: enrichItem(action.payload.nextAnchor),
          },
        };
      } else if (setSelectableItemsAction.match(action)) {
        updatedAction = {
          ...action,
          payload: {
            selectableItems: action.payload.selectableItems.map(enrichItem),
            persist: action.payload.persist,
          },
        };
      }
    }

    const result = next(updatedAction);

    if (
      selectItemAction.match(updatedAction) ||
      unselectItemAction.match(updatedAction) ||
      selectAllSelectableItemsAction.match(updatedAction) ||
      selectItemsByMouseAction.match(updatedAction) ||
      selectShiftSelectedHighlightedItemsAction.match(updatedAction)
    ) {
      const state = store.getState();
      const selectedItems = state.selectedItems.selected;

      appStorage.setItem(PERSISTED_SELECTIONS, JSON.stringify(selectedItems));
    } else if (resetSelectedItemsAction.match(updatedAction)) {
      appStorage.removeItem(PERSISTED_SELECTIONS);
    }

    return result;
  };
